// frontend/src/services/common/index.js

const POSTGREST_RELATIVE_PATH = "/postgrest";
const GEOSERVER_RELATIVE_PATH = "/geoserver";
const GEO_DEFAULT_BASE_URL = "https://localhost/geoserver";
const TRAILING_SLASHES_REGEX = /\/+$/;
const EMPTY_STRING = "";
const AUTH_TOKEN_HEADER = "X-Auth-Token";
const AUTHORIZATION_HEADER = "Authorization";
const AUTHORIZATION_BEARER_PREFIX = "Bearer";
const TOKEN_WHITESPACE_REGEX = /\s+/g;
const POSTGREST_HOST_KEYWORD = "postgrest";
const POSTGREST_DIRECT_PORT_SIGNATURES = [":3300"];
const GEOSERVER_HOST_KEYWORD = "geoserver";
const GEOSERVER_DIRECT_PORT_SIGNATURES = [":8080", ":8484", ":8585"];
const HTTPS_PROTOCOL_PREFIX = "https://";
const WINDOW_ORIGIN_READ_ERROR_MESSAGE =
  "[Common Services] Falha ao ler window.location.origin";

const sanitizeUrl = (value) =>
  value ? value.replace(TRAILING_SLASHES_REGEX, EMPTY_STRING) : undefined;

const getWindowOrigin = () => {
  if (typeof window === "undefined") {
    return undefined;
  }

  try {
    return window.location?.origin;
  } catch (error) {
    console.warn(WINDOW_ORIGIN_READ_ERROR_MESSAGE, error);
    return undefined;
  }
};

const matchesDirectPostgrestSignature = (value) =>
  Boolean(
    value &&
      (value.endsWith(POSTGREST_RELATIVE_PATH) ||
        value.includes(POSTGREST_HOST_KEYWORD) ||
        POSTGREST_DIRECT_PORT_SIGNATURES.some((signature) =>
          value.endsWith(signature)
        ))
  );

const buildPostgrestUrlFromBase = (baseUrl) => {
  if (!baseUrl) {
    return undefined;
  }

  return matchesDirectPostgrestSignature(baseUrl)
    ? baseUrl
    : `${baseUrl}${POSTGREST_RELATIVE_PATH}`;
};

const matchesDirectGeoserverSignature = (value) =>
  Boolean(
    value &&
      (value.endsWith(GEOSERVER_RELATIVE_PATH) ||
        value.includes(GEOSERVER_HOST_KEYWORD) ||
        GEOSERVER_DIRECT_PORT_SIGNATURES.some((signature) =>
          value.endsWith(signature)
        ))
  );

const buildGeoserverUrlFromBase = (baseUrl) => {
  if (!baseUrl) {
    return undefined;
  }

  return matchesDirectGeoserverSignature(baseUrl)
    ? baseUrl
    : `${baseUrl}${GEOSERVER_RELATIVE_PATH}`;
};

const isSecureUrl = (value) => value?.startsWith(HTTPS_PROTOCOL_PREFIX);

const selectPreferredUrl = (candidates, preferSecure) => {
  if (!preferSecure) {
    return candidates.find(Boolean);
  }

  return candidates.find(isSecureUrl) || candidates.find(Boolean);
};

export const sanitizeAuthToken = (token) => {
  if (typeof token !== "string") {
    return EMPTY_STRING;
  }

  return token.replace(TOKEN_WHITESPACE_REGEX, EMPTY_STRING);
};

export const buildAuthHeaders = (token) => {
  const sanitizedToken = sanitizeAuthToken(token);

  if (!sanitizedToken) {
    return {};
  }

  return {
    [AUTH_TOKEN_HEADER]: sanitizedToken,
    [AUTHORIZATION_HEADER]: AUTHORIZATION_BEARER_PREFIX + " " + sanitizedToken,
  };
};

export const buildAuthConfig = (token) => {
  const headers = buildAuthHeaders(token);

  if (!Object.keys(headers).length) {
    return {};
  }

  return { headers };
};

const sanitizedBaseUrl = sanitizeUrl(process.env.REACT_APP_HOST);
const sanitizedPostgrestHost = sanitizeUrl(process.env.REACT_APP_POSTGREST_HOST);
const sanitizedGeoserverHost = sanitizeUrl(process.env.REACT_APP_GEOSERVER_HOST);
const sanitizedWindowOrigin = sanitizeUrl(getWindowOrigin());

const fallbackPostgrestUrl = buildPostgrestUrlFromBase(sanitizedBaseUrl);
const runtimePostgrestUrl = buildPostgrestUrlFromBase(sanitizedWindowOrigin);
const fallbackGeoserverUrl = buildGeoserverUrlFromBase(sanitizedBaseUrl);
const runtimeGeoserverUrl = buildGeoserverUrlFromBase(sanitizedWindowOrigin);

const preferSecureUrls = isSecureUrl(sanitizedWindowOrigin);

export const BASE_URL = selectPreferredUrl(
  [sanitizedBaseUrl, sanitizedWindowOrigin],
  preferSecureUrls
);

export const POSTGREST_URL = selectPreferredUrl(
  [
    sanitizedPostgrestHost,
    fallbackPostgrestUrl,
    runtimePostgrestUrl,
  ],
  preferSecureUrls
);

export const BASE_URL_GEO = selectPreferredUrl(
  [sanitizedGeoserverHost, fallbackGeoserverUrl, runtimeGeoserverUrl, GEO_DEFAULT_BASE_URL],
  preferSecureUrls
);

export const AUTH_TOKEN_HEADER_NAME = AUTH_TOKEN_HEADER;
